/**
 * App.jsx — PriceBuddy Public Dashboard
 * by IponBuddy · Community Price Map
 *
 * reCAPTCHA v3 integration:
 *   - Site key is read from the environment variable:
 *       Vite  →  VITE_RECAPTCHA_SITE_KEY  (in .env: VITE_RECAPTCHA_SITE_KEY=6Lc...)
 *       CRA   →  REACT_APP_RECAPTCHA_SITE_KEY
 *   - Token is obtained before every search request and every product
 *     detail fetch, then forwarded to the API so your backend can verify
 *     the score via Google's siteverify endpoint.
 *   - The reCAPTCHA badge is hidden via CSS (allowed per Google's policy as long
 *     as you display the branding text in your UI — already in the footer).
 */

import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense  } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, ReferenceLine, CartesianGrid,
} from 'recharts';
import {
  searchPrices, getPriceTrend, comparePrices, getHotProducts,
  getPlatformStats, fmtPrice, fmtChange, currencyFor,
  COUNTRIES, LOCATIONS_BY_COUNTRY,
} from './api';

import { Routes, Route } from 'react-router-dom';
import { PrivacyPage, TermsPage } from './LegalPages';


import logo from './assets/favicon.png';

const WorldMapPage = lazy(() =>
  import('./WorldMapPage').then(m => ({ default: m.WorldMapPage }))
);

// ─── reCAPTCHA site key ────────────────────────────────────────────────────────
// Vite exposes env vars prefixed with VITE_; CRA uses REACT_APP_.
const RECAPTCHA_SITE_KEY =
  import.meta.env?.VITE_RECAPTCHA_SITE_KEY ??       // Vite
  process.env?.REACT_APP_RECAPTCHA_SITE_KEY ??       // CRA
  '';

if (!RECAPTCHA_SITE_KEY) {
  console.warn(
    '[PriceBuddy] reCAPTCHA site key not found. ' +
    'Add VITE_RECAPTCHA_SITE_KEY (Vite) or REACT_APP_RECAPTCHA_SITE_KEY (CRA) to your .env file.',
  );
}

// ─── useRecaptcha hook ─────────────────────────────────────────────────────────
/**
 * Loads the reCAPTCHA v3 script once and returns a stable
 * `executeRecaptcha(action)` function.
 *
 * Usage:
 *   const executeRecaptcha = useRecaptcha();
 *   const token = await executeRecaptcha('search');
 *   // send `token` to your backend for verification
 */
function useRecaptcha() {
  const readyRef = useRef(false);

  // Inject the script tag exactly once
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY || document.getElementById('recaptcha-script')) return;

    const script = document.createElement('script');
    script.id  = 'recaptcha-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.grecaptcha.ready(() => { readyRef.current = true; });
    };
    document.head.appendChild(script);

    return () => {
      // Leave the script in place — removing it mid-session causes grecaptcha errors.
    };
  }, []);

  /**
   * Returns a reCAPTCHA v3 token for the given action string.
   * Returns null if the key is missing or the library hasn't loaded yet.
   */
  const executeRecaptcha = useCallback(async (action = 'generic') => {
    if (!RECAPTCHA_SITE_KEY) return null;

    // Wait up to 3 s for the library to become ready
    const grecaptcha = await new Promise((resolve, reject) => {
      const start = Date.now();
      const check = () => {
        if (window.grecaptcha?.execute) return resolve(window.grecaptcha);
        if (Date.now() - start > 3000) return reject(new Error('reCAPTCHA not ready'));
        setTimeout(check, 100);
      };
      check();
    }).catch((err) => {
      console.warn('[reCAPTCHA]', err.message);
      return null;
    });

    if (!grecaptcha) return null;

    try {
      return await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
    } catch (err) {
      console.warn('[reCAPTCHA] execute failed:', err.message);
      return null;
    }
  }, []);

  return executeRecaptcha;
}

// ─── Debounce hook ─────────────────────────────────────────────────────────────
function useDebounce(val, ms = 350) {
  const [d, setD] = useState(val);
  useEffect(() => {
    const t = setTimeout(() => setD(val), ms);
    return () => clearTimeout(t);
  }, [val, ms]);
  return d;
}

// ─── Dynamic SEO hook ──────────────────────────────────────────────────────────
function useSEO({ title, description, canonical }) {
  useEffect(() => {
    document.title = title
      ? `${title} · PriceBuddy by IponBuddy`
      : 'PriceBuddy · Community Grocery Price Tracker | IponBuddy';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && description) metaDesc.setAttribute('content', description);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle && title) ogTitle.setAttribute('content', `${title} · PriceBuddy`);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc && description) ogDesc.setAttribute('content', description);
    const link = document.querySelector('link[rel="canonical"]');
    if (link && canonical) link.setAttribute('href', canonical);
    return () => { document.title = 'PriceBuddy · Community Grocery Price Tracker | IponBuddy'; };
  }, [title, description, canonical]);
}

const pct = fmtChange;

// ─── Palette ───────────────────────────────────────────────────────────────────
const T = {
  bg:    '#F9F6F0', bg1:   '#F3EFE6', bg2:  '#EBE6DA', white: '#FFFDF9',
  ink:   '#18160F', sub:   '#6B6459', dim:  '#A89F93',
  line:  'rgba(24,22,15,0.08)', line2: 'rgba(24,22,15,0.14)',
  g:     '#185C33', gL:    '#E8F2EC', gM:   '#3A9E62', gPop: '#1F7D44',
  a:     '#A86200', aL:    '#FDF3DC', aBdr: '#E8C97A',
  r:     '#B52A1A', rL:    '#FDF0ED', rBdr: '#F0B8B0',
};

// ─── Global CSS ────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,700;1,9..144,300;1,9..144,500&family=Instrument+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap');
:root{--serif:'Fraunces',Georgia,serif;--sans:'Instrument Sans',system-ui,sans-serif;--mono:'DM Mono','Courier New',monospace;--ease:cubic-bezier(.25,.46,.45,.94);}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html{font-size:16px;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;}
body{background:${T.bg};color:${T.ink};font-family:var(--sans);line-height:1.6;min-height:100vh;}
a{color:inherit;text-decoration:none;}
button{font-family:var(--sans);cursor:pointer;border:none;background:none;}
input{font-family:var(--sans);}
::selection{background:${T.gL};color:${T.g};}
::-webkit-scrollbar{width:4px;height:4px;}
::-webkit-scrollbar-track{background:transparent;}
::-webkit-scrollbar-thumb{background:${T.bg2};border-radius:4px;}
::-webkit-scrollbar-thumb:hover{background:${T.dim};}
@keyframes ticker{0%{transform:translateX(0);}100%{transform:translateX(-50%);}}
@keyframes pulse{0%,100%{opacity:1;transform:scale(1);}50%{opacity:.4;transform:scale(.75);}}
@keyframes fadeUp{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}
@keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes spin{to{transform:rotate(360deg);}}
@keyframes shimmer{0%{background-position:-800px 0;}100%{background-position:800px 0;}}
.bone{background:linear-gradient(90deg,${T.bg1} 25%,${T.bg} 50%,${T.bg1} 75%);background-size:1600px 100%;animation:shimmer 1.6s ease infinite;border-radius:4px;}
.ticker-track{animation:ticker var(--ticker-duration,50s) linear infinite;}
.ticker-wrap:hover .ticker-track{animation-play-state:paused;}
.result-grid{display:grid;grid-template-columns:1fr 140px 140px 100px 60px;gap:12px;}
@media(max-width:680px){
  .result-grid{grid-template-columns:1fr 100px 60px;}
  .result-grid .col-store,.result-grid .col-location{display:none;}
}
@media(max-width:440px){.hide-xs{display:none!important;}}
/* Hide the reCAPTCHA badge — branding text is displayed in the footer instead,
   which is permitted by Google's Terms of Service. */
.grecaptcha-badge{visibility:hidden!important;opacity:0!important;pointer-events:none!important;}
`;

function GlobalStyles() {
  return <style dangerouslySetInnerHTML={{ __html: CSS }} />;
}

// ─── Micro atoms ───────────────────────────────────────────────────────────────
function Bone({ w = '100%', h = 12, r = 4, style = {} }) {
  return <div className="bone" style={{ width: w, height: h, borderRadius: r, flexShrink: 0, ...style }} />;
}

function Spin({ size = 15 }) {
  return (
    <span style={{
      display: 'inline-block', width: size, height: size, flexShrink: 0,
      border: `1.5px solid ${T.bg2}`, borderTopColor: T.g,
      borderRadius: '50%', animation: 'spin .55s linear infinite',
    }} />
  );
}

function Live() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: T.gL, border: `1px solid ${T.gM}44`, borderRadius: 99, padding: '3px 9px 3px 7px' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.gPop, display: 'inline-block', animation: 'pulse 1.8s ease-in-out infinite' }} />
      <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: T.g, letterSpacing: 0.8, fontWeight: 500 }}>LIVE</span>
    </span>
  );
}

function Delta({ value, size = 'sm' }) {
  if (value == null) return null;
  const up  = value > 0;
  const clr = up ? T.r  : T.g;
  const bg  = up ? T.rL : T.gL;
  const bdr = up ? T.rBdr : '#9DD4B2';
  const fs  = size === 'lg' ? 13 : 11;
  return (
    <span title={up ? 'Price increased' : 'Price decreased'} style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      fontFamily: 'var(--mono)', fontSize: fs, fontWeight: 500,
      color: clr, background: bg, border: `1px solid ${bdr}`,
      borderRadius: 5, padding: size === 'lg' ? '3px 9px' : '2px 6px',
      whiteSpace: 'nowrap', flexShrink: 0, cursor: 'default',
    }}>
      <span style={{ fontSize: fs - 1, lineHeight: 1 }}>{up ? '↑' : '↓'}</span>
      {pct(Math.abs(value))}
    </span>
  );
}

// ─── Ticker ────────────────────────────────────────────────────────────────────
function Ticker({ items = [], currency = 'PHP' }) {
  if (!items.length) return null;
  const doubled  = [...items, ...items];
  const duration = `${Math.max(items.length * 4, 20)}s`;
  return (
    <div className="ticker-wrap" style={{ background: T.g, overflow: 'hidden', padding: '8px 0', position: 'relative', userSelect: 'none', borderBottom: '1px solid rgba(0,0,0,0.15)' }}>
      {['left', 'right'].map(side => (
        <div key={side} style={{ position: 'absolute', top: 0, bottom: 0, [side]: 0, width: 48, zIndex: 2, pointerEvents: 'none', background: `linear-gradient(to ${side === 'left' ? 'right' : 'left'}, ${T.g}ee, transparent)` }} />
      ))}
      <div className="ticker-track" style={{ display: 'flex', gap: 32, whiteSpace: 'nowrap', width: 'max-content', '--ticker-duration': duration }}>
        {doubled.map((item, i) => {
          const isUp = item.direction === 'up';
          return (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'rgba(255,255,255,0.55)', letterSpacing: 0.3 }}>
                {(item.product_name ?? '').toUpperCase().slice(0, 22)}
              </span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: '#fff', fontWeight: 500 }}>
                {fmtPrice(item.recent_avg ?? item.avg_price, currency)}
              </span>
              {item.change_pct != null && (
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: isUp ? '#FFA69E' : '#7DFFC2' }}>
                  {isUp ? '+' : '−'}{Math.abs(item.change_pct).toFixed(1)}%
                </span>
              )}
              <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: 10 }}>·</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ─── Trend chart ───────────────────────────────────────────────────────────────
const CustomDot = () => null;
function TrendChart({ data = [], color, currency = 'PHP' }) {
  if (!data.length) return (
    <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.bg1, borderRadius: 8 }}>
      <span style={{ fontSize: 12, color: T.dim, fontFamily: 'var(--mono)' }}>— no data yet —</span>
    </div>
  );
  const prices = data.map(d => d.avg_price).filter(Boolean);
  const min = Math.min(...prices), max = Math.max(...prices);
  return (
    <ResponsiveContainer width="100%" height={100}>
      <LineChart data={data} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} stroke={T.line} strokeDasharray="0" />
        <XAxis dataKey="date" hide />
        <YAxis domain={[min * 0.96, max * 1.04]} hide />
        <Tooltip
          contentStyle={{ background: T.white, border: `1px solid ${T.line2}`, borderRadius: 8, fontSize: 12, fontFamily: 'var(--sans)', padding: '8px 12px' }}
          labelStyle={{ color: T.sub, fontSize: 11, marginBottom: 2 }}
          itemStyle={{ color: T.ink, fontFamily: 'var(--mono)', fontWeight: 500 }}
          formatter={(v) => [fmtPrice(v, currency), '']} separator=""
        />
        {(min + max) / 2 > 0 && <ReferenceLine y={(min + max) / 2} stroke={T.line2} strokeDasharray="4 4" />}
        <Line type="monotone" dataKey="avg_price" stroke={color} strokeWidth={1.75} dot={<CustomDot />}
          activeDot={{ r: 4, fill: color, stroke: T.white, strokeWidth: 2 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── Hot product card ──────────────────────────────────────────────────────────
function HotCard({ item, index, onSelect, currency = 'PHP' }) {
  const [hov, setHov] = useState(false);
  const isMover = item.is_mover !== false;
  const down  = item.direction === 'down';
  const color = isMover ? (down ? T.g : T.r) : T.g;
  const pctVal = isMover
    ? (down ? -Math.abs(item.change_pct ?? 0) : Math.abs(item.change_pct ?? 0))
    : null;

  return (
    <button
      onClick={() => onSelect(item)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: '100%', textAlign: 'left', background: T.white,
        border: `1px solid ${hov ? T.line2 : T.line}`, borderRadius: 12,
        padding: '18px 18px 16px', cursor: 'pointer',
        transition: 'all .18s var(--ease)',
        transform: hov ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hov ? '0 8px 28px rgba(24,22,15,0.09)' : '0 1px 4px rgba(24,22,15,0.05)',
        position: 'relative', overflow: 'hidden',
        animation: `fadeUp .3s var(--ease) ${index * 40}ms both`,
      }}
    >
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: color, borderRadius: '12px 0 0 12px' }} />
      <div style={{ paddingLeft: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 14 }}>
          <span style={{ fontFamily: 'var(--serif)', fontSize: 14.5, fontWeight: 500, color: T.ink, lineHeight: 1.25, flex: 1 }}>
            {item.product_name}
          </span>
          {isMover
            ? <Delta value={pctVal} />
            : item.store_count
              ? <span style={{ fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 500, color: T.g, background: T.gL, border: `1px solid #9DD4B2`, borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {item.store_count} {item.store_count === 1 ? 'store' : 'stores'}
                </span>
              : null
          }
        </div>
        {isMover ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: T.dim, letterSpacing: 0.3, marginBottom: 2 }}>was</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 13, color: T.sub, textDecoration: 'line-through' }}>{fmtPrice(item.prior_avg, currency)}</div>
            </div>
            <div style={{ width: 20, height: 1, background: `linear-gradient(to right, ${T.dim}, ${color})`, flexShrink: 0 }} />
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color, letterSpacing: 0.3, fontWeight: 500, marginBottom: 2 }}>now</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 18, fontWeight: 500, color }}>{fmtPrice(item.recent_avg, currency)}</div>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: T.dim, letterSpacing: 0.3, marginBottom: 4 }}>current price</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 20, fontWeight: 500, color: T.g }}>{fmtPrice(item.avg_price, currency)}</div>
          </div>
        )}
        {(item.store_name || item.location) && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.line}`, fontSize: 11.5, color: T.sub, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11 }}>📍</span>
            {item.store_name}
            {item.store_name && item.location ? <span style={{ color: T.dim }}> · </span> : null}
            {item.location}
          </div>
        )}
      </div>
    </button>
  );
}

// ─── Store comparison row ──────────────────────────────────────────────────────
function StoreRow({ row, rank, currency = 'PHP' }) {
  const best = rank === 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', background: best ? T.gL : 'transparent', borderBottom: `1px solid ${T.line}` }}>
      <div style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 500, background: best ? T.g : T.bg1, color: best ? '#fff' : T.sub }}>{rank + 1}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.store_name}</div>
        <div style={{ fontSize: 11, color: T.sub, marginTop: 1 }}>📍 {row.location}</div>
      </div>
      {best && (
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9.5, fontWeight: 500, letterSpacing: 0.6, color: T.g, background: '#fff', border: `1px solid ${T.gM}55`, borderRadius: 4, padding: '2px 7px', flexShrink: 0 }}>CHEAPEST</span>
      )}
      <div style={{ fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 500, color: best ? T.g : T.ink, flexShrink: 0 }}>{fmtPrice(row.price, currency)}</div>
    </div>
  );
}

// ─── Product detail modal ──────────────────────────────────────────────────────
// Accepts an optional `recaptchaToken` prop — forwarded to the compare/trend APIs.
function DetailModal({ product, country, location, currency, onClose, recaptchaToken }) {
  const [compare, setCompare] = useState([]);
  const [trend,   setTrend]   = useState([]);
  const [busy,    setBusy]    = useState(true);

  const productKey = product?.product_name_normalized ?? product?.product_name ?? null;

  useSEO({
    title: product ? product.product_name : null,
    description: product
      ? `Compare ${product.product_name} prices across stores${location ? ` in ${location}` : ''}. Community-sourced data from IponBuddy.`
      : null,
  });

  useEffect(() => {
    if (!productKey) return;

    setBusy(true);
    setCompare([]);
    setTrend([]);

    let cancelled = false;

    Promise.allSettled([
      // Pass the recaptchaToken so your API can verify it server-side.
      comparePrices(productKey, { country, location: location || null, recaptchaToken }),
      getPriceTrend(productKey, { days: 30, country, location: location || null, recaptchaToken }),
    ]).then(([c, t]) => {
      if (cancelled) return;
      if (c.status === 'fulfilled') setCompare(c.value?.data ?? []);
      if (t.status === 'fulfilled') setTrend(t.value?.data ?? []);
    }).finally(() => {
      if (!cancelled) setBusy(false);
    });

    window.history.pushState({ modal: true }, '');
    const handlePop = () => onClose();
    window.addEventListener('popstate', handlePop);

    return () => {
      cancelled = true;
      window.removeEventListener('popstate', handlePop);
    };
  }, [productKey, country, location, recaptchaToken]); // onClose intentionally omitted

  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [onClose]);

  if (!product) return null;
  const prices     = trend.map(d => d.avg_price).filter(Boolean);
  const trendColor = prices.length < 2 ? T.g : prices[prices.length - 1] >= prices[0] ? T.r : T.g;

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(24,22,15,0.48)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, animation: 'fadeIn .16s ease' }}>
      <div style={{ background: T.bg, border: `1px solid ${T.line2}`, borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 32px 80px rgba(24,22,15,0.22)', animation: 'fadeUp .2s ease' }}>
        <div style={{ padding: '20px 22px 18px', borderBottom: `1px solid ${T.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: 20, fontWeight: 500, color: T.ink, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.product_name}</h2>
            <p style={{ fontSize: 12, color: T.sub, marginTop: 5 }}>
              {product.scan_count != null ? `${product.scan_count} reports` : 'Community data'}
              {location ? ` · 📍 ${location}` : product.location ? ` · 📍 ${product.location}` : ''}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: T.bg1, border: `1px solid ${T.line}`, color: T.sub, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <div style={{ padding: '18px 22px 0' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 0.8, color: T.dim, fontWeight: 500, marginBottom: 10 }}>30-DAY PRICE TREND</div>
            {busy ? <Bone h={100} r={8} /> : <TrendChart data={trend} color={trendColor} currency={currency} />}
          </div>
          <div style={{ paddingTop: 20 }}>
            <div style={{ padding: '0 22px 10px', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 0.8, color: T.dim, fontWeight: 500 }}>STORE COMPARISON</div>
            <div style={{ borderTop: `1px solid ${T.line}` }}>
              {busy
                ? [1,2,3].map(i => <div key={i} style={{ padding: '12px 20px', borderBottom: `1px solid ${T.line}` }}><Bone h={40} r={6} /></div>)
                : compare.length === 0
                  ? <div style={{ padding: '28px 22px', textAlign: 'center', color: T.sub, fontSize: 13 }}>No community price data yet.</div>
                  : compare.map((row, i) => <StoreRow key={i} row={row} rank={i} currency={currency} />)
              }
            </div>
          </div>
          <div style={{ margin: '18px 22px 22px', background: T.gL, border: '1px solid #9DD4B2', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 130 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 3 }}>🔔 Watch this price</p>
              <p style={{ fontSize: 12, color: T.sub }}>Get alerts when it drops — download IponBuddy.</p>
            </div>
            <a href="https://play.google.com/store/apps/details?id=com.iponbuddy.app" target="_blank" rel="noreferrer"
              style={{ background: T.g, color: '#fff', borderRadius: 8, padding: '9px 16px', fontSize: 12, fontWeight: 600, display: 'inline-block', flexShrink: 0, whiteSpace: 'nowrap' }}>
              Get the app ↗
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ value, label, loading }) {
  if (!loading && value == null) return null;
  return (
    <div>
      {loading
        ? <Bone w={80} h={28} style={{ marginBottom: 4, borderRadius: 6 }} />
        : <div style={{ fontFamily: 'var(--serif)', fontSize: 24, fontWeight: 500, color: T.g, lineHeight: 1 }}>
            {new Intl.NumberFormat('en').format(value)}+
          </div>
      }
      <div style={{ fontSize: 12, color: T.sub, marginTop: 4 }}>{label}</div>
    </div>
  );
}

// ─── Country + Location picker ─────────────────────────────────────────────────
function FilterPicker({ country, location, onCountryChange, onLocationChange }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('country');
  const ref = useRef(null);
  const currentCountry = COUNTRIES.find(c => c.code === country) ?? COUNTRIES[0];
  const locations      = LOCATIONS_BY_COUNTRY[country] ?? [];

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  return (
    <div ref={ref} onMouseDown={e => e.stopPropagation()} style={{ position: 'relative', display: 'flex', flexShrink: 0 }}>
      <button onClick={e => { e.stopPropagation(); setView('country'); setOpen(true); }} aria-label="Select country"
        style={{ height: '100%', padding: '0 10px', background: T.bg1, borderLeft: `1px solid ${T.line}`, color: T.ink, fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', borderRadius: 0 }}>
        <span style={{ fontSize: 14 }}>{currentCountry.flag}</span>
        <span className="hide-xs" style={{ fontSize: 12 }}>{currentCountry.code}</span>
        <span style={{ color: T.dim, fontSize: 9 }}>▼</span>
      </button>
      <button onClick={e => { e.stopPropagation(); setView('location'); setOpen(true); }} aria-label="Select location"
        style={{ height: '100%', padding: '0 14px', background: T.bg1, borderLeft: `1px solid ${T.line}`, color: T.ink, fontSize: 13, display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', borderRadius: '0 9px 9px 0' }}>
        📍 <span className="hide-xs">{location || 'All areas'}</span>
        <span style={{ color: T.dim, fontSize: 9 }}>▼</span>
      </button>

      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 100, background: T.white, border: `1.5px solid ${T.line2}`, borderRadius: 10, minWidth: 200, maxHeight: 300, overflowY: 'auto', boxShadow: '0 12px 36px rgba(24,22,15,0.14)', animation: 'fadeUp .14s ease' }}>
          <div style={{ display: 'flex', borderBottom: `1px solid ${T.line}`, position: 'sticky', top: 0, background: T.white, zIndex: 1 }}>
            {[{ key: 'country', label: '🌏 Country' }, { key: 'location', label: '📍 City / Area' }].map(t => (
              <button key={t.key} onClick={() => setView(t.key)} style={{ flex: 1, padding: '9px 0', fontSize: 12, fontWeight: view === t.key ? 600 : 400, color: view === t.key ? T.ink : T.sub, borderBottom: `2px solid ${view === t.key ? T.g : 'transparent'}`, marginBottom: -1, background: 'transparent', transition: 'color .12s' }}>{t.label}</button>
            ))}
          </div>
          {view === 'country' && COUNTRIES.map(c => (
            <button key={c.code} onClick={() => { onCountryChange(c.code); onLocationChange(''); setView('location'); }}
              style={{ width: '100%', textAlign: 'left', padding: '9px 14px', border: 'none', fontSize: 13, background: country === c.code ? T.gL : 'transparent', color: country === c.code ? T.g : T.ink, fontWeight: country === c.code ? 600 : 400, display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${T.line}`, cursor: 'pointer' }}>
              <span style={{ fontSize: 16 }}>{c.flag}</span>
              <span style={{ flex: 1 }}>{c.name}</span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: T.dim }}>{c.currency}</span>
            </button>
          ))}
          {view === 'location' && (
            <>
              <button onClick={() => { onLocationChange(''); setOpen(false); }} style={{ width: '100%', textAlign: 'left', padding: '9px 14px', border: 'none', fontSize: 13, cursor: 'pointer', background: !location ? T.gL : 'transparent', color: !location ? T.g : T.ink, fontWeight: !location ? 600 : 400, borderBottom: `1px solid ${T.line}` }}>
                All areas in {currentCountry.name}
              </button>
              {locations.map(loc => (
                <button key={loc} onClick={() => { onLocationChange(loc); setOpen(false); }}
                  style={{ width: '100%', textAlign: 'left', padding: '9px 14px', border: 'none', fontSize: 13, cursor: 'pointer', background: location === loc ? T.gL : 'transparent', color: location === loc ? T.g : T.ink, fontWeight: location === loc ? 600 : 400, borderBottom: `1px solid ${T.line}` }}>
                  {loc}
                </button>
              ))}
              {locations.length === 0 && <div style={{ padding: '20px 14px', fontSize: 12, color: T.dim, textAlign: 'center' }}>No predefined areas for this country yet.</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Search result header + row ────────────────────────────────────────────────
function ResultHeader() {
  return (
    <div className="result-grid" style={{ padding: '9px 18px', background: T.bg1, borderBottom: `1px solid ${T.line}` }}>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 9.5, fontWeight: 500, letterSpacing: 0.8, color: T.dim }}>PRODUCT</div>
      <div className="col-store"    style={{ fontFamily: 'var(--mono)', fontSize: 9.5, fontWeight: 500, letterSpacing: 0.8, color: T.dim }}>STORE</div>
      <div className="col-location" style={{ fontFamily: 'var(--mono)', fontSize: 9.5, fontWeight: 500, letterSpacing: 0.8, color: T.dim }}>LOCATION</div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 9.5, fontWeight: 500, letterSpacing: 0.8, color: T.dim, textAlign: 'right' }}>AVG PRICE</div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 9.5, fontWeight: 500, letterSpacing: 0.8, color: T.dim, textAlign: 'right' }}>REPORTS</div>
    </div>
  );
}

function ResultRow({ row, isLast, onSelect, currency = 'PHP' }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={() => onSelect(row)} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      className="result-grid"
      style={{ padding: '13px 18px', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer', background: hov ? T.bg1 : 'transparent', borderBottom: isLast ? 'none' : `1px solid ${T.line}`, alignItems: 'center', transition: 'background .12s' }}>
      <div style={{ fontSize: 13.5, fontWeight: 500, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.product_name}</div>
      <div className="col-store"    style={{ fontSize: 12, color: T.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.store_name || '—'}</div>
      <div className="col-location" style={{ fontSize: 12, color: T.sub }}>{row.location ? `📍 ${row.location}` : '—'}</div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 500, color: T.g, textAlign: 'right' }}>{fmtPrice(row.avg_price, currency)}</div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: T.dim, textAlign: 'right' }}>{row.scan_count ?? '—'}</div>
    </button>
  );
}

function Empty({ icon, heading, sub }) {
  return (
    <div style={{ background: T.white, borderRadius: 12, border: `1px solid ${T.line}`, padding: '56px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>{icon}</div>
      <h3 style={{ fontFamily: 'var(--serif)', fontSize: 17, fontWeight: 500, color: T.ink, marginBottom: 8 }}>{heading}</h3>
      <p style={{ fontSize: 13, color: T.sub }}>{sub}</p>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    { icon: '📷', n: '01', title: 'Scan a receipt',   body: 'IponBuddy users photograph grocery receipts directly in the app.' },
    { icon: '🤖', n: '02', title: 'AI reads prices',  body: 'OCR extracts product names, prices, and store info automatically.' },
    { icon: '🤝', n: '03', title: 'Community shares', body: 'With opt-in consent, data feeds the public map — no personal info.' },
    { icon: '🔔', n: '04', title: 'Get notified',     body: 'Watch any product and receive a push notification when the price changes.' },
  ];
  return (
    <section style={{ marginTop: 72 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 28, borderBottom: `1px solid ${T.line}`, paddingBottom: 16 }}>
        <h2 style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 500, color: T.ink }}>How it works</h2>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: T.dim }}>crowdsourced · no ads · free forever</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
        {steps.map((s, i) => (
          <div key={s.n} style={{ background: T.white, borderRadius: 12, border: `1px solid ${T.line}`, padding: '20px 18px', animation: `fadeUp .4s var(--ease) ${i * 60}ms both` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <span style={{ fontSize: 26 }}>{s.icon}</span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 500, color: T.dim, letterSpacing: 0.5 }}>{s.n}</span>
            </div>
            <h3 style={{ fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 500, color: T.ink, marginBottom: 8 }}>{s.title}</h3>
            <p style={{ fontSize: 13, color: T.sub, lineHeight: 1.6 }}>{s.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function AppBanner() {
  const tags = ['Free', 'Anonymous', 'No data sold', '🇵🇭 PH-built'];
  return (
    <div style={{ marginTop: 40, marginBottom: 48, background: T.g, borderRadius: 16, padding: 36, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 28, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 230 }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 500, letterSpacing: 1, color: 'rgba(255,255,255,0.5)', marginBottom: 12 }}>CONTRIBUTE TO THE MAP</div>
        <h2 style={{ fontFamily: 'var(--serif)', fontSize: 26, fontWeight: 500, color: '#fff', marginBottom: 10, lineHeight: 1.2 }}>Scan a receipt.<br />Help a neighbor.</h2>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.68)', lineHeight: 1.7, maxWidth: 400 }}>Every receipt scanned on IponBuddy feeds this map. The more people scan, the more accurate prices become for everyone.</p>
        <div style={{ display: 'flex', gap: 6, marginTop: 16, flexWrap: 'wrap' }}>
          {tags.map(t => <span key={t} style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.85)', background: 'rgba(255,255,255,0.12)', borderRadius: 5, padding: '3px 10px' }}>{t}</span>)}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 }}>
        <a href="https://play.google.com/store/apps/details?id=com.iponbuddy.app" target="_blank" rel="noreferrer"
          style={{ background: '#fff', color: T.g, borderRadius: 10, padding: '13px 24px', fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
          📱 Google Play
        </a>
        <span title="App Store version coming soon" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', color: 'rgba(255,255,255,0.45)', borderRadius: 10, padding: '13px 24px', fontWeight: 500, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, cursor: 'not-allowed' }}>
          App Store <span style={{ fontSize: 11, marginLeft: 2 }}>soon</span>
        </span>
      </div>
    </div>
  );
}

// ─── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [query,           setQuery]           = useState('');
  const [country,         setCountry]         = useState('PH');
  const [location,        setLocation]        = useState('');
  const [hotItems,        setHotItems]        = useState([]);
  const [results,         setResults]         = useState([]);
  const [selected,        setSelected]        = useState(null);
  const [selectedToken,   setSelectedToken]   = useState(null); // reCAPTCHA token for the open modal
  const [hotBusy,         setHotBusy]         = useState(true);
  const [searching,       setSearching]       = useState(false);
  const [tab,             setTab]             = useState('hot');
  const [searchFocus,     setSearchFocus]     = useState(false);
  const [stats,           setStats]           = useState({ products: null, reports: null, cities: null, stores: null });
  const [statsLoaded,     setStatsLoaded]     = useState(false);

  const currency        = useMemo(() => currencyFor(country), [country]);
  const dQ              = useDebounce(query, 420);
  const executeRecaptcha = useRecaptcha();

  const hasMoverItems = useMemo(() => hotItems.some(i => i.is_mover), [hotItems]);

  const seoTitle = query.length >= 2 ? `"${query}" grocery prices` : location ? `Grocery prices in ${location}` : null;
  const seoDesc  = query.length >= 2
    ? `Community grocery prices for "${query}" across ${location || 'all areas'}. Compare stores. Updated in real-time.`
    : 'Compare real grocery prices from community receipts. No ads. Straight from the palengke.';
  useSEO({ title: !selected ? seoTitle : null, description: !selected ? seoDesc : null });

  const closeDetail = useCallback(() => {
    setSelected(null);
    setSelectedToken(null);
  }, []);

  // When user clicks a product card: get a token, then open the modal.
  const handleSelect = useCallback(async (item) => {
    const token = await executeRecaptcha('view_product');
    setSelectedToken(token);
    setSelected(item);
  }, [executeRecaptcha]);

  // Stats on mount / country change
  useEffect(() => {
    getPlatformStats(country)
      .then(s => { setStats(s); setStatsLoaded(true); })
      .catch(() => setStatsLoaded(true));
  }, [country]);

  // Hot products — abortable
  useEffect(() => {
    const controller = new AbortController();
    let cancelled    = false;

    setHotBusy(true);
    setHotItems([]);

    getHotProducts(location || null, country, controller.signal)
      .then(r  => { if (!cancelled) setHotItems(r?.data ?? []); })
      .catch(err => { if (!cancelled && err?.name !== 'AbortError') setHotItems([]); })
      .finally(() => { if (!cancelled) setHotBusy(false); });

    return () => { cancelled = true; controller.abort(); };
  }, [location, country]);

  // Search — abortable, protected by reCAPTCHA v3
  useEffect(() => {
    if (!dQ || dQ.length < 2) {
      setResults([]); setSearching(false);
      if (!dQ) setTab('hot');
      return;
    }

    setTab('search');
    setSearching(true);

    const controller = new AbortController();
    let cancelled    = false;

    // Obtain a fresh token for every search action, then fire the request.
    executeRecaptcha('search').then((recaptchaToken) => {
      if (cancelled) return;

      searchPrices(dQ, {
        location: location || null,
        country,
        signal: controller.signal,
        recaptchaToken, // forward to your API layer
      })
        .then(r  => { if (!cancelled) setResults(r?.data ?? []); })
        .catch(err => { if (!cancelled && err?.name !== 'AbortError') setResults([]); })
        .finally(() => { if (!cancelled && !controller.signal.aborted) setSearching(false); });
    });

    return () => { cancelled = true; controller.abort(); };
  }, [dQ, location, country, executeRecaptcha]);

  const clearQuery = useCallback(() => {
    setQuery(''); setTab('hot'); setResults([]); setSearching(false);
  }, []);

  const handleCountryChange = useCallback((code) => {
    setCountry(code);
    setLocation('');
    setResults([]);
    setTab(query.length >= 2 ? 'search' : 'hot');
  }, [query]);

  const countryMeta = COUNTRIES.find(c => c.code === country);

  const hotSubtitle = hotBusy
    ? 'Loading prices…'
    : hasMoverItems
      ? `Products with notable price changes${location ? ` · 📍 ${location}` : ''}${country !== 'PH' ? ` · ${countryMeta?.flag ?? ''} ${country}` : ''}`
      : `Most tracked products${location ? ` · 📍 ${location}` : ''}${country !== 'PH' ? ` · ${countryMeta?.flag ?? ''} ${country}` : ''}`;

  return (
    <Routes>
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms"   element={<TermsPage />} />
      <Route
  path="/world-price"
  element={
    <Suspense fallback={
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: 14, color: '#6B6459' }}>
        Loading map…
      </div>
    }>
      <WorldMapPage />
    </Suspense>
  }
/>
      <Route path="*" element={
        <>
          <GlobalStyles />
          <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>

            <Ticker items={hotItems} currency={currency} />

            {/* NAV */}
            <nav style={{ borderBottom: `1px solid ${T.line}`, background: T.bg }}>
              <div style={{ maxWidth: 1080, margin: '0 auto', padding: '0 24px', height: 58, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <img src={logo} alt="PriceBuddy" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain', flexShrink: 0 }} />
                  <div>
                    <span style={{ fontFamily: 'var(--serif)', fontSize: 17.5, fontWeight: 500, color: T.ink, letterSpacing: -0.2 }}>PriceBuddy</span>
                    <span className="hide-xs" style={{ fontFamily: 'var(--mono)', fontSize: 10, color: T.dim, marginLeft: 8, letterSpacing: 0.3 }}>by IponBuddy</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* <Live /> */}
                  <a href="https://iponbuddy.vincedaniel.com" target="_blank" rel="noreferrer" className="hide-xs"
                    style={{ fontSize: 12.5, color: T.sub, padding: '6px 12px', border: `1px solid ${T.line2}`, borderRadius: 7, background: T.white }}>
                    Get IponBuddy ↗
                  </a>
                </div>
              </div>
            </nav>

            {/* HERO */}
            <header style={{ padding: '56px 24px 0', maxWidth: 1080, margin: '0 auto', width: '100%' }}>
              <div style={{ maxWidth: 640, marginBottom: 40 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: T.aL, border: `1px solid ${T.aBdr}`, borderRadius: 6, padding: '4px 12px', marginBottom: 22 }}>
                  <span style={{ fontSize: 12 }}>🧾</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: T.a, fontWeight: 500, letterSpacing: 0.3 }}>REAL PRICES FROM REAL RECEIPTS</span>
                </div>
                <h1 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(32px, 4.5vw, 54px)', fontWeight: 300, color: T.ink, lineHeight: 1.12, letterSpacing: -1.5, marginBottom: 18 }}>
                  Compare grocery prices<br />
                  <em style={{ color: T.g, fontStyle: 'italic', fontWeight: 500 }}>across the world.</em>
                </h1>
                <p style={{ fontSize: 15.5, color: T.sub, lineHeight: 1.7, maxWidth: 500 }}>
                  Community prices from IponBuddy users' grocery receipts. No ads. No sponsored listings. Straight from the palengke.
                </p>
              </div>

              {/* SEARCH BAR */}
              <div style={{ display: 'flex', gap: 0, maxWidth: 700, background: T.white, border: `1.5px solid ${searchFocus ? T.g : T.line2}`, borderRadius: 11, overflow: 'visible', boxShadow: searchFocus ? `0 0 0 3px ${T.gL}` : '0 2px 12px rgba(24,22,15,0.07)', transition: 'border-color .18s, box-shadow .18s', position: 'relative' }}>
                <div style={{ padding: '0 14px', display: 'flex', alignItems: 'center', color: searching ? T.g : T.dim, flexShrink: 0, fontSize: 16 }}>
                  {searching ? <Spin /> : '🔍'}
                </div>
                <input
                  type="text" value={query} onChange={e => setQuery(e.target.value)}
                  onFocus={() => setSearchFocus(true)} onBlur={() => setSearchFocus(false)}
                  placeholder="Lucky Me, Milo, Rice…"
                  aria-label="Search grocery products" autoComplete="off" spellCheck="false"
                  style={{ flex: 1, padding: '15px 0', background: 'transparent', border: 'none', outline: 'none', color: T.ink, fontSize: 15, lineHeight: 1, minWidth: 0 }}
                />
                {query && (
                  <button onClick={clearQuery} aria-label="Clear search"
                    style={{ padding: '0 12px', color: T.dim, fontSize: 16, display: 'flex', alignItems: 'center', flexShrink: 0 }}>✕</button>
                )}
                <FilterPicker country={country} location={location} onCountryChange={handleCountryChange} onLocationChange={setLocation} />
              </div>

              {/* STATS */}
              <div style={{ display: 'flex', gap: 36, marginTop: 32, flexWrap: 'wrap' }}>
                <StatCard value={stats.products} label="products tracked"  loading={!statsLoaded} />
                <StatCard value={stats.reports}  label="community reports" loading={!statsLoaded} />
                <StatCard value={stats.cities}   label="cities"            loading={!statsLoaded} />
                <StatCard value={stats.stores}   label="stores"            loading={!statsLoaded} />
              </div>
            </header>

            {/* CONTENT */}
            <main style={{ maxWidth: 1080, margin: '44px auto 0', width: '100%', padding: '0 24px', flex: 1 }}>

              {/* Tab strip */}
              <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${T.line}`, marginBottom: 28 }}>
                {[{ key: 'hot', label: 'Price movers' }, { key: 'search', label: 'Search results' }].map(t => {
                  const active = tab === t.key;
                  return (
                    <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: '10px 0', marginRight: 28, border: 'none', background: 'transparent', fontSize: 14, fontWeight: active ? 600 : 400, color: active ? T.ink : T.sub, borderBottom: `2px solid ${active ? T.g : 'transparent'}`, marginBottom: -1, transition: 'color .15s', display: 'flex', alignItems: 'center', gap: 7 }}>
                      {t.label}
                      {t.key === 'search' && searching && <Spin size={12} />}
                      {t.key === 'search' && !searching && results.length > 0 && (
                        <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, background: T.gL, color: T.g, borderRadius: 10, padding: '1px 6px' }}>{results.length}</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* HOT TAB */}
              {tab === 'hot' && (
                <div>
                  <div style={{ marginBottom: 20 }}>
                    <h2 style={{ fontFamily: 'var(--serif)', fontSize: 19, fontWeight: 500, color: T.ink }}>
                      {hotBusy ? 'Price movers' : hasMoverItems ? 'Price movers' : 'Popular products'}
                    </h2>
                    <p style={{ fontSize: 12, color: T.sub, marginTop: 3 }}>{hotSubtitle}</p>
                  </div>

                  {hotBusy ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(255px, 1fr))', gap: 10 }}>
                      {[1,2,3,4,5,6].map(i => (
                        <div key={i} style={{ background: T.white, borderRadius: 12, padding: '18px 18px 16px', border: `1px solid ${T.line}` }}>
                          <Bone w="60%" h={13} style={{ marginBottom: 14 }} />
                          <Bone w="40%" h={22} />
                        </div>
                      ))}
                    </div>
                  ) : hotItems.length === 0 ? (
                    <Empty icon="📊" heading="No products yet" sub="Scan a receipt on IponBuddy to add the first price." />
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(255px, 1fr))', gap: 10 }}>
                      {hotItems.map((item, i) => (
                        <HotCard key={`${item.product_name_normalized ?? item.product_name}-${i}`} item={item} index={i} onSelect={handleSelect} currency={currency} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* SEARCH TAB */}
              {tab === 'search' && (
                <div>
                  <div style={{ marginBottom: 20 }}>
                    <h2 style={{ fontFamily: 'var(--serif)', fontSize: 19, fontWeight: 500, color: T.ink }}>{query ? `"${query}"` : 'Search results'}</h2>
                    <p style={{ fontSize: 12, color: T.sub, marginTop: 3 }}>
                      {searching ? 'Searching community data…' : `${results.length} product${results.length !== 1 ? 's' : ''} found`}
                    </p>
                  </div>
                  {!query && <Empty icon="🔍" heading="Start typing" sub="Search any grocery product to see community prices." />}
                  {query.length === 1 && !searching && <Empty icon="⌨️" heading="Keep typing…" sub="Enter at least 2 characters to search." />}
                  {searching && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[1,2,3,4].map(i => (
                        <div key={i} style={{ background: T.white, borderRadius: 10, padding: 16, border: `1px solid ${T.line}`, display: 'flex', gap: 14, alignItems: 'center' }}>
                          <div style={{ flex: 1 }}><Bone w="50%" h={13} style={{ marginBottom: 8 }} /><Bone w="28%" h={11} /></div>
                          <Bone w={64} h={18} />
                        </div>
                      ))}
                    </div>
                  )}
                  {!searching && results.length > 0 && (
                    <div style={{ background: T.white, borderRadius: 12, border: `1px solid ${T.line}`, overflow: 'hidden', boxShadow: '0 1px 6px rgba(24,22,15,0.05)' }}>
                      <ResultHeader />
                      {results.map((r, i) => <ResultRow key={`${r.product_name}-${i}`} row={r} isLast={i === results.length - 1} onSelect={handleSelect} currency={currency} />)}
                    </div>
                  )}
                  {!searching && query.length >= 2 && results.length === 0 && (
                    <Empty icon="🤷" heading={`Nothing for "${query}"`} sub="Be the first to scan this product on IponBuddy!" />
                  )}
                </div>
              )}

              <HowItWorks />
              <AppBanner />
            </main>

            {/* FOOTER — reCAPTCHA branding required when badge is hidden */}
            <footer style={{ borderTop: `1px solid ${T.line}`, padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: '8px 16px' }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: T.dim }}>© 2026 IponBuddy</span>
              <span style={{ color: T.line2, fontSize: 10 }}>·</span>
              <span style={{ fontSize: 11, color: T.dim }}>Community-sourced prices. Not guaranteed to reflect current shelf prices.</span>
              <span style={{ color: T.line2, fontSize: 10 }}>·</span>
              <a href="/privacy" style={{ fontSize: 11, color: T.sub }}>Privacy</a>
              <span style={{ color: T.line2, fontSize: 10 }}>·</span>
               <a href="/world-price" style={{ fontSize: 11, color: T.sub }}>World Price</a>
              <span style={{ color: T.line2, fontSize: 10 }}>·</span>
              <a href="/terms" style={{ fontSize: 11, color: T.sub }}>Terms</a>
              <span style={{ color: T.line2, fontSize: 10 }}>·</span>
              {/* Required disclosure when the reCAPTCHA badge is hidden (per Google ToS) */}
              <span style={{ fontSize: 11, color: T.dim }}>
                Protected by{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" style={{ color: T.sub, textDecoration: 'underline' }}>reCAPTCHA</a>
                {' '}·{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" style={{ color: T.sub, textDecoration: 'underline' }}>Google ToS</a>
              </span>
            </footer>
          </div>

          {selected && (
            <DetailModal
              product={selected}
              country={country}
              location={location}
              currency={currency}
              onClose={closeDetail}
              recaptchaToken={selectedToken}
            />
          )}
        </>
      } />
    </Routes>
  );
}